import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import type { AgendaEvent, AgendaRoom } from "@domaddress/shared";

interface Props {
  event: AgendaEvent;
  onEventUpdated: (ev: AgendaEvent) => void;
}

type RoomDraft = {
  room_id: number;
  start_datetime: string;
  end_datetime: string;
  notes: string;
};

function toLocalInput(dt: string): string {
  if (!dt) return "";
  return dt.replace(" ", "T").slice(0, 16);
}

function fromLocalInput(v: string): string {
  return v.replace("T", " ");
}

export default function EventRoomPlanner({ event, onEventUpdated }: Props) {
  const [rooms, setRooms] = useState<AgendaRoom[]>([]);
  const [drafts, setDrafts] = useState<RoomDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newRoomId, setNewRoomId] = useState("");

  const loadRooms = useCallback(async () => {
    const res = await api.get<{ data: AgendaRoom[] }>("/api/rooms");
    setRooms(res.data.data);
  }, []);

  useEffect(() => {
    void loadRooms();
  }, [loadRooms]);

  useEffect(() => {
    const existing = event.rooms ?? [];
    setDrafts(
      existing.map((r) => ({
        room_id: r.room_id,
        start_datetime: toLocalInput(r.start_datetime),
        end_datetime: toLocalInput(r.end_datetime),
        notes: r.notes ?? "",
      }))
    );
  }, [event.rooms, event.start_datetime, event.end_datetime]);

  function addRoom() {
    const rid = Number(newRoomId);
    if (!rid) return;
    if (drafts.some((d) => d.room_id === rid)) {
      setError("Deze zaal staat al op het evenement.");
      return;
    }
    setError("");
    setDrafts([
      ...drafts,
      {
        room_id: rid,
        start_datetime: toLocalInput(event.start_datetime),
        end_datetime: toLocalInput(event.end_datetime),
        notes: "",
      },
    ]);
    setNewRoomId("");
  }

  function removeRoom(roomId: number) {
    setDrafts(drafts.filter((d) => d.room_id !== roomId));
  }

  function updateDraft(roomId: number, patch: Partial<RoomDraft>) {
    setDrafts(drafts.map((d) => (d.room_id === roomId ? { ...d, ...patch } : d)));
  }

  async function save() {
    setSaving(true);
    setError("");
    try {
      const res = await api.put<{ data: AgendaEvent }>(`/api/events/${event.id}`, {
        rooms: drafts.map((d) => ({
          room_id: d.room_id,
          start_datetime: fromLocalInput(d.start_datetime),
          end_datetime: fromLocalInput(d.end_datetime),
          notes: d.notes || null,
        })),
      });
      onEventUpdated(res.data.data);
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setError(msg ?? "Opslaan mislukt. Controleer of de zaal vrij is.");
    } finally {
      setSaving(false);
    }
  }

  const roomName = (id: number) => rooms.find((r) => r.id === id)?.name ?? `Zaal #${id}`;

  return (
    <div className="card event-room-planner">
      <h2>Zaalboekingen</h2>
      <p className="page-subtitle" style={{ marginTop: 0 }}>
        Een evenement kan meerdere zalen tegelijk boeken. Tijden kunnen afwijken van het evenement
        (bijv. voor opbouw of schoonmaak).
      </p>

      {drafts.length === 0 && <p>Nog geen zalen gekoppeld.</p>}

      {drafts.map((d) => (
        <div key={d.room_id} className="roster-role-card event-room-card">
          <div className="event-room-card-header">
            <strong>{roomName(d.room_id)}</strong>
            <button type="button" className="btn-text-danger" onClick={() => removeRoom(d.room_id)}>
              Verwijderen
            </button>
          </div>
          <div className="form-row">
            <label>Start zaal</label>
            <input
              type="datetime-local"
              value={d.start_datetime}
              onChange={(e) => updateDraft(d.room_id, { start_datetime: e.target.value })}
            />
          </div>
          <div className="form-row">
            <label>Einde zaal</label>
            <input
              type="datetime-local"
              value={d.end_datetime}
              onChange={(e) => updateDraft(d.room_id, { end_datetime: e.target.value })}
            />
          </div>
          <div className="form-row">
            <label>Notities</label>
            <input
              value={d.notes}
              onChange={(e) => updateDraft(d.room_id, { notes: e.target.value })}
              placeholder="Optioneel"
            />
          </div>
        </div>
      ))}

      <div
        className="event-room-add"
        style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "1rem" }}
      >
        <select value={newRoomId} onChange={(e) => setNewRoomId(e.target.value)}>
          <option value="">Zaal toevoegen…</option>
          {rooms.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <button type="button" className="btn-secondary" onClick={addRoom} disabled={!newRoomId}>
          Toevoegen
        </button>
        <button type="button" className="btn-primary" onClick={() => void save()} disabled={saving}>
          {saving ? "Opslaan…" : "Zalen opslaan"}
        </button>
      </div>

      {error && <p className="form-error">{error}</p>}
    </div>
  );
}
